import { Component } from '@angular/core';

import { AlertDialogConfig } from '../../error/alert/alert-dialog.component';
import { ClrAlertModule } from '@clr/angular';

@Component({
  selector: 'app-toast-container',
  template: `
    @for (toast of toasts; track toast) {
      <clr-alert
        [clrAlertType]="toast.type"
        [clrAlertClosable]="true"
        class="alert-toast"
        (clrAlertClosedChange)="remove(toast)"
        >
        <clr-alert-item>
          <span class="alert-text">
            <strong>{{ toast.title }}:</strong> {{ toast.message }}
            @if (hasDetails(toast)) {
              <div class="error-details">
                <ul>
                  @for (detail of toast.details; track detail) {
                    <li [innerHTML]="formatDetail(detail)"></li>
                  }
                </ul>
              </div>
            }
          </span>
        </clr-alert-item>
      </clr-alert>
    }
    `,
  styles: [
    `
      :host {
        position: fixed;
        bottom: 1rem;
        right: 1rem;
        z-index: 1050;
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
        max-width: 90%;
        max-height: 80vh;
        overflow-y: auto;
      }
      .error-details {
        margin-top: 0.5rem;
        font-size: 0.9em;
        white-space: pre-wrap;
        word-break: break-word;
        max-height: 300px;
        overflow-y: auto;
      }
      .error-details ul {
        margin: 0;
        padding-left: 1.5rem;
      }
      .error-details li {
        margin-bottom: 0.5rem;
      }
      .alert-toast {
        max-width: 100%;
        width: 500px;
      }
      .pre-formatted {
        font-family: monospace;
        white-space: pre-wrap;
        background-color: rgba(0, 0, 0, 0.05);
        padding: 2px 4px;
        border-radius: 2px;
      }
    `,
  ],
  imports: [ClrAlertModule],
})
export class ToastContainerComponent {
  toasts: AlertDialogConfig[] = [];
  readonly MAX_TOASTS = 3;

  show(config: AlertDialogConfig) {
    // 1. Déduplication : Vérifier si un toast identique existe déjà
    const existingIndex = this.toasts.findIndex(
      t => t.title === config.title && t.message === config.message,
    );

    if (existingIndex !== -1) {
      // Si le toast existe déjà, on ne fait rien (ou on pourrait le déplacer à la fin pour reset le timer)
      // Ici, on choisit de ne pas le dupliquer pour éviter le spam visuel
      return;
    }

    // 2. Limite du nombre de toasters
    if (this.toasts.length >= this.MAX_TOASTS) {
      // Supprimer le plus ancien (le premier de la liste)
      this.toasts.shift();
    }

    this.toasts.push(config);
    const timeout = this.hasDetails(config) ? 15000 : 5000;
    setTimeout(() => this.remove(config), timeout);
  }

  remove(toast: AlertDialogConfig) {
    this.toasts = this.toasts.filter(t => t !== toast);
  }

  hasDetails(toast: AlertDialogConfig): boolean {
    return Array.isArray(toast.details) && toast.details.length > 0;
  }

  formatDetail(detail: any): string {
    if (detail === null || detail === undefined) {
      return '';
    }

    const detailStr = typeof detail === 'string' ? detail : this.convertToString(detail);

    if (detailStr.includes('{') && detailStr.includes('}')) {
      const jsonStart = detailStr.indexOf('{');

      const prefix = detailStr.substring(0, jsonStart);

      let jsonPart = detailStr.substring(jsonStart);

      try {
        const jsonObj = JSON.parse(jsonPart);
        jsonPart = JSON.stringify(jsonObj, null, 2);
        jsonPart = this.escapeHtml(jsonPart);
        return `${prefix}<span class="pre-formatted">${jsonPart}</span>`;
      } catch (_e) {
        return this.escapeHtml(detailStr);
      }
    }

    return this.escapeHtml(detailStr);
  }

  private convertToString(value: any): string {
    if (typeof value === 'object' && value !== null) {
      try {
        return JSON.stringify(value);
      } catch (_e) {
        return String(value);
      }
    }
    return String(value);
  }

  private escapeHtml(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;')
      .replace(/\n/g, '<br>');
  }
}
